import { create } from 'zustand';
import api from '../services/api';

// Fisher-Yates shuffle (ne mute pas le tableau original)
function shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

// Build docs map from questions array, indexé par questionId.
// Chaque entrée contient :
//   - doc_path / doc_name  : ancien single-doc legacy (compat)
//   - documents[]          : nouveau multi-doc (priorité sur le single si non vide)
//   - sous_sujet_label     : pour le titre du viewer
function buildDocsMap(questions) {
    const map = {};
    const ssDoc = {};
    for (const q of questions) {
        if (!q.sous_sujet_id || ssDoc[q.sous_sujet_id]) continue;
        const hasMulti = Array.isArray(q.documents) && q.documents.length > 0;
        if (q.doc_path || hasMulti) {
            ssDoc[q.sous_sujet_id] = {
                doc_path: q.doc_path || null,
                doc_name: q.doc_name || null,
                documents: hasMulti ? q.documents : [],
                sous_sujet_label: q.sous_sujet_label || null,
            };
        }
    }
    for (const q of questions) {
        const doc = ssDoc[q.sous_sujet_id];
        if (doc) map[q.id] = doc;
    }
    return Object.keys(map).length > 0 ? map : null;
}

// Libellé lisible pour une session transverse (review/refaire), d'après l'origine.
// Évite d'afficher le nom du seul sous-sujet d'ancrage pour un quiz multi-sujets.
const TRANSVERSE_LABELS = {
    melange: 'Quiz transversal',
    'audit-parcours': 'Bilan ciblé',
    'audit-diagnostic': 'Bilan guidé',
    'smart-mixed': 'Quiz intelligent',
    'smart-weaknesses': 'Faiblesses',
    'smart-revision': 'Révision',
    'smart-consolidation': 'Consolidation',
    'errors-never': 'Jamais réussies',
    'errors-sometimes': 'Instables',
    'errors-recent': 'Erreurs récentes',
};
function transverseLabel(origin) {
    if (!origin || origin === 'subject' || origin.startsWith('history:')) return null;
    if (TRANSVERSE_LABELS[origin]) return TRANSVERSE_LABELS[origin];
    if (origin.startsWith('smart-')) return 'Quiz intelligent';
    if (origin.startsWith('errors-')) return 'Quiz sur erreurs';
    return null;
}

// Source des questions envoyée à l'API (answers/smart-answers/history) : elle
// décide de la TABLE lue côté serveur. Une thématique publiée vit dans
// `partner_questions` ; annoncer 'official' pour elle fait lire `questions` avec
// des identifiants qui n'y correspondent pas → explications et bonnes réponses
// d'autres questions. Tous les lanceurs doivent passer par ici.
const sourceOf = ({ isPartner = false, isCustom = false } = {}) =>
    (isPartner ? 'partner' : (isCustom ? 'custom' : 'official'));

const useQuizStore = create((set, get) => ({
    // Etat
    quizInfo: null,        // { id, label, description }
    parcoursSlug: null,
    scopeId: null,         // scope dans lequel le quiz est joue
    questionIds: [],       // ordered list of question IDs for current quiz
    questions: [],
    answers: {},           // { questionId: 'a'|'b'|... }
    correctAnswers: null,  // { questionId: { correct, explication } }
    currentIndex: 0,
    timeInSeconds: 0,
    timeLimit: null,       // Mélange classique : temps max en secondes (migration 261), sinon null
    targetPct: null,       // Mélange classique : objectif de score en %, sinon null
    plannedTotal: null,    // « S'arrêter là » : taille de la séance lancée (bilan « séance incomplète »), sinon null
    loading: false,
    finished: false,
    retiredCount: 0,      // questions retirées (suspendues, désactivées) ôtées à la clôture
    finalizing: false,
    isReview: false,
    isSmartQuiz: false,    // quiz adaptatif (pas lie a un sous-sujet fixe)
    smartQuizMeta: null,   // { scope_id, quiz_mode, breakdown }
    historyId: null, parcoursSlug: null,
    mode: 'quiz',          // 'quiz' | 'apprentissage'
    origin: 'subject',     // 'subject' | 'history:{id}'
    isCustomQuiz: false,   // true when quiz is on user-generated content
    questionSource: 'official', // 'official' | 'custom' | 'partner'
    docsMap: null,         // { questionId: { doc_path, doc_name, sous_sujet_label } } for multi-sujet quizzes
    error: null,           // string error message for user-facing display
    responseTimes: {},     // { questionId: seconds } — temps de réponse pour SM-2
    _questionShownAt: 0,   // timestamp ms de l'affichage de la question courante
    _lastAnswerEvent: null,
    _lastAnswerContext: null,
    sessionId: null,       // id de la ligne quiz_current_sessions en cours (null = pas encore persistée)
    _saveInFlight: null,
    _completionAttempt: null,
    _runId: 0, // invalide les réponses réseau d'une séance quittée ou remplacée
    _sessionCreating: false, // garde anti-double-INSERT pendant la création initiale

    // Demarrer un quiz (par sous-sujet)
    // options: { isCustom: bool, isPartner: bool }
    startQuiz: async (sousSujetId, scopeId, nbQuestions = 10, quizMode = 'quiz', questionIdsOverride = null, originOverride = 'subject', options = {}) => {
        const isCustomQuiz = !!options.isCustom;
        const questionSource = options.isPartner ? 'partner' : (isCustomQuiz ? 'custom' : 'official');
        const isSmartResume = originOverride.startsWith('smart-');
        set({ _runId: get()._runId + 1, finalizing: false, _completionAttempt: null, retiredCount: 0, loading: true, error: null, finished: false, isReview: false, correctAnswers: null, historyId: null, mode: quizMode, origin: originOverride, scopeId, isCustomQuiz, questionSource, parcoursSlug: options.parcours ?? null,
              isSmartQuiz: isSmartResume, smartQuizMeta: isSmartResume ? { scope_id: scopeId, quiz_mode: originOverride.replace('smart-', ''), breakdown: null } : null });
        try {
            let questions;
            let questionIds;
            let sousSujetInfo;
            let savedSession = null;

            if (questionIdsOverride) {
                // Refaire: charger des questions specifiques
                const { data: qData } = await api.get('/qcm/questions.php', {
                    params: { sous_sujet_id: sousSujetId, scope_id: scopeId, question_ids: JSON.stringify(questionIdsOverride) }
                });
                questions = qData.data.questions;
                sousSujetInfo = qData.data.sous_sujet;
                questionIds = questionIdsOverride;
                // Shuffle les reponses
                questions = questions.map(q => ({
                    ...q,
                    reponses: q.reponses ? shuffleArray(q.reponses) : q.reponses,
                }));
            } else {
                // Tenter de reprendre une session existante
                const { data: sessionData } = await api.get('/qcm/session.php', { params: { sous_sujet_id: sousSujetId, scope_id: scopeId, origin: originOverride } });
                savedSession = sessionData.data;

                if (savedSession?.mode) quizMode = savedSession.mode;

                if (savedSession && savedSession.question_ids && savedSession.question_ids.length > 0) {
                    // Session existante: charger ces questions specifiques
                    const { data: qData } = await api.get('/qcm/questions.php', {
                        params: { sous_sujet_id: sousSujetId, scope_id: scopeId, question_ids: JSON.stringify(savedSession.question_ids) }
                    });
                    questions = qData.data.questions;
                    sousSujetInfo = qData.data.sous_sujet;
                    // Sync questionIds with actually returned questions (some may have been deleted)
                    const returnedIds = new Set(questions.map(q => q.id));
                    questionIds = savedSession.question_ids.filter(id => returnedIds.has(id));
                    // Adjust currentIndex if it exceeds available questions
                    if (savedSession.current_question_index >= questions.length) {
                        savedSession.current_question_index = Math.max(0, questions.length - 1);
                    }
                } else {
                    // Nouvelle session: charger N questions aleatoires
                    const { data: qData } = await api.get('/qcm/questions.php', {
                        params: { sous_sujet_id: sousSujetId, scope_id: scopeId, n: nbQuestions }
                    });
                    questions = qData.data.questions;
                    sousSujetInfo = qData.data.sous_sujet;
                    questionIds = questions.map(q => q.id);
                    // Shuffle les reponses pour nouvelle session
                    questions = questions.map(q => ({
                        ...q,
                        reponses: q.reponses ? shuffleArray(q.reponses) : q.reponses,
                    }));
                }
            }

            // En mode apprentissage, precharger les reponses
            let correctAnswers = null;
            if (quizMode === 'apprentissage' && questionIds.length > 0) {
                const answerParams = { sous_sujet_id: sousSujetId, scope_id: scopeId, question_ids: questionIds.join(','), ...(options.parcours != null ? { parcours: options.parcours } : {}) };
                const { data: aData } = await api.get('/qcm/answers.php', { params: answerParams });
                correctAnswers = aData.data.answers;
            }

            // For smart quiz resume, override label
            const SMART_RESUME_LABELS = {
                weaknesses: 'Faiblesses',
                revision: 'Revision',
                consolidation: 'Consolidation',
                mixed: 'Progression intelligente',
            };
            const smartMode = isSmartResume ? originOverride.replace('smart-', '') : null;
            // Transverse si origine connue OU si les questions couvrent plusieurs sous-sujets.
            const distinctSSStart = new Set(questions.map(q => q.sous_sujet_id).filter(Boolean));
            const tLabel = transverseLabel(originOverride) || (distinctSSStart.size > 1 ? 'Quiz transversal' : null);
            const quizInfoFinal = isSmartResume
                ? { label: SMART_RESUME_LABELS[smartMode] || 'Quiz intelligent', id: sousSujetInfo?.id || sousSujetId }
                : (tLabel && sousSujetInfo ? { ...sousSujetInfo, label: tLabel } : sousSujetInfo);

            set({
                quizInfo: quizInfoFinal,
                questionIds,
                questions,
                answers: savedSession?.answers || {},
                currentIndex: savedSession?.current_question_index || 0,
                timeInSeconds: savedSession?.time_in_seconds || 0,
                correctAnswers,
                mode: quizMode,
                loading: false,
                responseTimes: {},
                _questionShownAt: Date.now(),
                // Reprise → on cible la ligne existante par son id ; nouvelle session → null
                // (le 1er _autoSave fera l'INSERT et mémorisera l'id).
                sessionId: savedSession?.id ?? null,
            });

            // Sauvegarder immédiatement si nouvelle session (aucune session existante)
            // pour que la liste des questions soit persistée même sans répondre
            if (!savedSession) get()._autoSave();

            return true;
        } catch (err) {
            const status = err?.response?.status;
            set({ loading: false, accessDenied: status === 403 });
            return false;
        }
    },

    // Repondre a une question — capture aussi le temps de réponse pour SM-2
    setAnswer: (questionId, answer) => {
        const { answers, responseTimes, _questionShownAt } = get();
        const newAnswers = { ...answers, [questionId]: answer };
        // Delta depuis l'affichage de la question. Capé entre 1 et 300s
        // pour ignorer les sessions où l'user a laissé l'onglet ouvert.
        let delta = null;
        if (_questionShownAt > 0) {
            const seconds = Math.round((Date.now() - _questionShownAt) / 1000);
            if (seconds >= 1 && seconds <= 300) delta = seconds;
        }
        const newResponseTimes = delta !== null
            ? { ...responseTimes, [questionId]: delta }
            : responseTimes;
        set({ answers: newAnswers, responseTimes: newResponseTimes,
            _lastAnswerEvent: { event_id: crypto.randomUUID(), question_id: questionId, selected_answer: answer },
            _lastAnswerContext: get().quizInfo,
        });
        get()._autoSave();
    },

    // Naviguer — reset le timer à chaque changement de question
    goToQuestion: (index) => {
        set({ currentIndex: index, _questionShownAt: Date.now() });
        get()._autoSave();
    },

    nextQuestion: () => {
        const { currentIndex, questions } = get();
        if (currentIndex < questions.length - 1) {
            set({ currentIndex: currentIndex + 1, _questionShownAt: Date.now() });
            get()._autoSave();
        }
    },

    prevQuestion: () => {
        const { currentIndex } = get();
        if (currentIndex > 0) {
            set({ currentIndex: currentIndex - 1, _questionShownAt: Date.now() });
            get()._autoSave();
        }
    },

    // Timer — auto-save every 30s to persist timer progress
    tick: () => set((s) => {
        const next = s.timeInSeconds + 1;
        if (next % 30 === 0) {
            setTimeout(() => get()._autoSave(), 0);
        }
        return { timeInSeconds: next };
    }),

    // Update a question in-place (after edit/save) and trigger re-render
    updateQuestion: (questionId, updatedFields) => set((s) => ({
        questions: s.questions.map(q =>
            q.id === questionId ? { ...q, ...updatedFields } : q
        ),
        correctAnswers: updatedFields.bonne_reponse && s.correctAnswers
            ? { ...s.correctAnswers, [questionId]: { ...s.correctAnswers[questionId], correct: updatedFields.bonne_reponse, explication: updatedFields.explication || s.correctAnswers[questionId]?.explication } }
            : s.correctAnswers,
    })),

    removeQuestion: (questionId) => set((s) => {
        const newQuestions = s.questions.filter(q => q.id !== questionId);
        const newIds = s.questionIds.filter(id => id !== questionId);
        const newAnswers = { ...s.answers };
        delete newAnswers[questionId];
        const idx = Math.min(s.currentIndex, Math.max(0, newQuestions.length - 1));
        return { questions: newQuestions, questionIds: newIds, answers: newAnswers, currentIndex: idx };
    }),

    // Terminer le quiz. partial = « S'arrêter là » : seules les questions répondues
    // sont enregistrées et restent affichées en revue.
    finishQuiz: async ({ partial = false } = {}) => {
        const { quizInfo, _runId, questionIds, answers, timeInSeconds, mode, origin, scopeId, questionSource, finished, loading, responseTimes } = get();
        if (!quizInfo || finished || loading) return;
        const questions = partial ? get().questions.filter(q => !!answers[q.id]) : get().questions;
        if (!questions.length) return;
        // Une réponse réseau tardive ne doit jamais terminer la séance suivante.
        const isCurrent = () => get()._runId === _runId && get().quizInfo === quizInfo;
        set({ loading: true });
        clearTimeout(get()._saveTimer); // annule un auto-save en attente (anti-résurrection)

        try {
            const sousSujetId = quizInfo.id;

            // Recuperer les bonnes reponses
            const parcoursSlug = get().parcoursSlug;
            const answerParams = { sous_sujet_id: sousSujetId, scope_id: scopeId, question_ids: questionIds.join(','), ...(parcoursSlug != null ? { parcours: parcoursSlug } : {}) };
            const { data } = await api.get('/qcm/answers.php', { params: answerParams });
            if (!isCurrent()) return null;
            const correctAnswers = data.data.answers;
            // Une question retirée en cours de quiz (suspendue, désactivée) n'a plus de
            // correction : elle sort du score et de l'historique, comme pour les séances
            // transverses (finishSmartQuiz). Toutes retirées : rien à enregistrer.
            const kept = questions.filter(q => correctAnswers[q.id]?.correct);
            if (!kept.length) {
                // La séance sauvegardée est supprimée aussi : sinon elle redeviendrait
                // reprenable si ses questions étaient remises en ligne.
                const sid = get().sessionId;
                await api.delete('/qcm/session.php', { data: sid ? { session_id: sid } : { sous_sujet_id: sousSujetId, scope_id: scopeId, origin } });
                if (!isCurrent()) return null;
                set({ finished: true, loading: false, correctAnswers, questions: [], questionIds: [], retiredCount: questions.length, sessionId: null });
                return { score: 0, total: 0 };
            }

            // Calculer le score + snapshot enrichi avec le temps de réponse par question
            let score = 0;
            const snapshot = {};
            for (const q of kept) {
                const given = answers[q.id] || null;
                const correct = correctAnswers[q.id]?.correct || null;
                if (given === correct) score++;
                const rt = responseTimes[q.id];
                snapshot[q.id] = rt ? { given, correct, response_time: rt } : { given, correct };
            }

            // Sauvegarder en base
            const histPayload = {
                last_answer_event: get()._lastAnswerContext === quizInfo ? get()._lastAnswerEvent : null,
                sous_sujet_id: sousSujetId,
                scope_id: scopeId,
                question_ids: kept.map(q => q.id),
                score,
                total: kept.length,
                answers_snapshot: snapshot,
                time_in_seconds: timeInSeconds,
                mode,
                origin,
            };
            if (questionSource !== 'official') histPayload.source = questionSource;
            const { data: histData } = await api.post('/qcm/history.php', histPayload);
            if (!isCurrent()) return null;

            // Le serveur ôte aussi les questions retirées entre les corrections et l'envoi :
            // l'écran affiche ce qu'il a enregistré.
            const recorded = histData.data?.recorded;
            const keptIds = new Set(recorded ? recorded.question_ids.map(Number) : kept.map(q => q.id));
            const shown = questions.filter(q => keptIds.has(Number(q.id)));
            const retiredCount = questions.length - shown.length;
            set({
                finished: true,
                loading: false,
                correctAnswers,
                historyId: histData.data?.history_id || 0,
                sessionId: null,
                ...(retiredCount || partial ? { questions: shown, questionIds: shown.map(q => q.id), currentIndex: 0, retiredCount } : {}),
            });

            return recorded ? { score: recorded.score, total: recorded.total } : { score, total: kept.length };
        } catch (err) {
            console.error('finishQuiz error:', err);
            if (isCurrent()) set({ loading: false, error: err.message || 'Erreur de sauvegarde' });
            return null;
        }
    },

    // Demarrer un quiz adaptatif (smart quiz)
    startSmartQuiz: async (scopeId, quizMode = 'mixed', nbQuestions = 15, { isCustom = false, isPartner = false, playMode = 'quiz', scopeImage = null } = {}) => {
        const questionSource = sourceOf({ isCustom, isPartner });
        set({ _runId: get()._runId + 1, finalizing: false, _completionAttempt: null, retiredCount: 0, loading: true, error: null, finished: false, isReview: false, correctAnswers: null,
              historyId: null, parcoursSlug: null, isSmartQuiz: true, mode: playMode,
              origin: 'smart-' + quizMode, scopeId, questionSource, sessionId: null });
        try {
            const { data } = await api.post('/qcm/smart-quiz.php', {
                scope_id: scopeId,
                mode: quizMode,
                nb_questions: nbQuestions,
            });

            const questions = data.data.questions.map(q => ({
                ...q,
                reponses: q.reponses ? shuffleArray(q.reponses) : q.reponses,
            }));

            const questionIds = questions.map(q => q.id);

            // Pre-load correct answers for apprentissage mode
            let correctAnswers = null;
            if (playMode === 'apprentissage' && questionIds.length > 0) {
                const { data: aData } = await api.post('/qcm/smart-answers.php', { scope_id: scopeId, ...(get().parcoursSlug != null ? { parcours: get().parcoursSlug } : {}), question_ids: questionIds, source: questionSource });
                correctAnswers = aData.data.answers;
            }

            const SMART_LABELS = {
                weaknesses: 'Faiblesses',
                revision: 'Revision',
                consolidation: 'Consolidation',
                mixed: 'Progression intelligente',
            };

            set({
                quizInfo: { label: SMART_LABELS[quizMode] || 'Quiz intelligent', id: null, matiere_image: scopeImage || null },
                questionIds,
                questions,
                answers: {},
                currentIndex: 0,
                timeInSeconds: 0,
                correctAnswers,
                docsMap: buildDocsMap(data.data.questions),
                smartQuizMeta: {
                    scope_id: scopeId,
                    quiz_mode: quizMode,
                    breakdown: data.data.breakdown,
                },
                loading: false,
            });

            // Save session immediately so it can be resumed
            get()._autoSave();

            return true;
        } catch (err) {
            set({ loading: false, isSmartQuiz: false });
            return false;
        }
    },

    // Démarre un quiz "Révisions du jour" (SM-2). Les questions retournées
    // sont celles dont next_review_at <= CURDATE() pour cet user dans ce scope.
    // Routé via le même finalize que les smart quiz (origin = 'smart-sm2-due').
    startSm2DueQuiz: async (scopeId, nbQuestions = 20, { isCustom = false, isPartner = false, playMode = 'quiz', scopeImage = null, parcours = null, guided = false } = {}) => {
        const questionSource = sourceOf({ isCustom, isPartner });
        // origin = 'smart-sm2-due' : active le bandeau de contexte par question
        // (Quiz.jsx > isTransversal). Sans ça, l'utilisateur ne voit pas à quel
        // sous-sujet appartient la question courante alors que SM-2 mélange.
        set({ _runId: get()._runId + 1, finalizing: false, _completionAttempt: null, retiredCount: 0, loading: true, error: null, finished: false, isReview: false, correctAnswers: null,
              historyId: null, parcoursSlug: parcours, isSmartQuiz: true, mode: playMode, origin: 'smart-sm2-due',
              scopeId, questionSource, sessionId: null });
        try {
            const { data } = await api.get('/qcm/sm2-due.php', { params: { scope_id: scopeId, limit: nbQuestions, ...(parcours != null ? { parcours } : {}), ...(guided ? { guided: 1 } : {}) } });
            const questions = (data.data.questions || []).map(q => ({
                ...q,
                reponses: q.reponses ? shuffleArray(q.reponses) : q.reponses,
            }));
            const questionIds = questions.map(q => q.id);

            if (questionIds.length === 0) {
                set({ loading: false, isSmartQuiz: false });
                return false;
            }

            // Pre-load correct answers in apprentissage mode
            let correctAnswers = null;
            if (playMode === 'apprentissage') {
                const { data: aData } = await api.post('/qcm/smart-answers.php', { scope_id: scopeId, ...(get().parcoursSlug != null ? { parcours: get().parcoursSlug } : {}), question_ids: questionIds, source: questionSource });
                correctAnswers = aData.data.answers;
            }

            set({
                quizInfo: { label: 'Révisions du jour', id: null, matiere_image: scopeImage || null },
                questionIds,
                questions,
                answers: {},
                currentIndex: 0,
                timeInSeconds: 0,
                correctAnswers,
                docsMap: buildDocsMap(data.data.questions || []),
                smartQuizMeta: {
                    scope_id: scopeId,
                    quiz_mode: 'sm2-due',
                    breakdown: {
                        due_today:   data.data.due_today || 0,
                        due_overdue: data.data.due_overdue || 0,
                        total:       data.data.total || questions.length,
                    },
                },
                loading: false,
            });

            get()._autoSave();
            return true;
        } catch (err) {
            set({ loading: false, isSmartQuiz: false });
            return false;
        }
    },

    // Terminer un smart quiz
    finishSmartQuiz: async ({ partial = false } = {}) => {
        const { quizInfo, _runId, parcoursSlug, answers, timeInSeconds, smartQuizMeta, mode, scopeId, origin, questionSource, responseTimes } = get();
        if (!smartQuizMeta || get().finished || get().finalizing) return;
        // « S'arrêter là » : le lot ne porte que les questions répondues (cf. history-batch.php).
        const questions = partial ? get().questions.filter(q => !!answers[q.id]) : get().questions;
        if (!questions.length) return;
        const isCurrent = () => get()._runId === _runId && get().quizInfo === quizInfo;
        const answerEvent = get()._lastAnswerContext === quizInfo ? get()._lastAnswerEvent : null;
        set({ finalizing: true });
        try {
            set({ error: null });
            clearTimeout(get()._saveTimer); // annule un auto-save en attente (anti-résurrection)

            // Recuperer les bonnes reponses via smart-answers (source explicite pour
            // éviter les collisions d'IDs entre questions et user_questions).
            // Try/catch : un 403 (accès expiré sur du contenu payant) ou une erreur réseau
            // ne doit PAS remonter en exception non gérée (→ écran blanc) ; on dégrade.
            const questionIds = questions.map(q => q.id);
            let correctAnswers;
            try {
                // L'API borne à 50 ; les anciennes séances couvrantes peuvent être
                // plus longues que les nouveaux bilans guidés.
                correctAnswers = {};
                for (let i = 0; i < questionIds.length; i += 50) {
                    const { data } = await api.post('/qcm/smart-answers.php', { scope_id: scopeId, ...(get().parcoursSlug != null ? { parcours: get().parcoursSlug } : {}), question_ids: questionIds.slice(i, i + 50), source: questionSource });
                    if (!isCurrent()) return null;
                    Object.assign(correctAnswers, data.data.answers);
                }
                // Une question retirée depuis le début (suspendue, désactivée) n'a plus de
                // correction : elle sort du score et du lot ; le serveur vérifie qu'elle est
                // bien retirée. Toutes retirées : lot vide, la séance est close sans résultat.
            } catch (err) {
                console.error('finishSmartQuiz error:', err);
                if (isCurrent()) set({ loading: false, error: err?.response?.status === 403 ? 'Accès expiré : impossible d\'enregistrer cette session.' : 'Erreur de sauvegarde' });
                return null;
            }

            const scored = questions.filter(q => correctAnswers[q.id]?.correct);
            // Calculer le score
            let score = 0;
            for (const q of scored) {
                const given = answers[q.id] || null;
                const correct = correctAnswers[q.id]?.correct || null;
                if (given === correct) score++;
            }

            // Determine history origin: error/melange quizzes keep their origin, smart quizzes use 'smart-{mode}'
            const historyOrigin = (origin?.startsWith('errors-') || origin === 'melange' || (origin === 'audit-parcours' || origin === 'audit-diagnostic'))
                ? origin
                : 'smart-' + (smartQuizMeta.quiz_mode || 'mixed');

            // Sauvegarder l'historique par sous-sujet
            const bySousSujet = {};
            for (const q of scored) {
                const ssId = q.sous_sujet_id;
                if (!bySousSujet[ssId]) bySousSujet[ssId] = [];
                bySousSujet[ssId].push(q);
            }

            const totalQ = scored.length;
            const entries = Object.entries(bySousSujet).map(([ssId, qs]) => {
                const snapshot = {};
                let qScore = 0;
                for (const q of qs) {
                    const given = answers[q.id] || null;
                    const correct = correctAnswers[q.id]?.correct || null;
                    if (given === correct) qScore++;
                    const rt = responseTimes[q.id];
                    snapshot[q.id] = rt ? { given, correct, response_time: rt } : { given, correct };
                }
                return { sous_sujet_id: Number(ssId), question_ids: qs.map(q => q.id),
                    score: qScore, total: qs.length, answers_snapshot: snapshot,
                    time_in_seconds: totalQ > 0 ? Math.round(qs.length / totalQ * timeInSeconds) : 0 };
            });
            let batchData = null;
            try {
                // Conserver la même tentative après un échec ou une réponse HTTP perdue.
                // Le serveur clôture tous les sujets en une transaction et déduplique par session.
                let attempt = get()._completionAttempt;
                if (!attempt || attempt.context !== quizInfo) {
                    await get()._saveSessionNow({ forFinalization: true });
                    if (!isCurrent()) return null;
                    attempt = { context: quizInfo, sessionId: get().sessionId };
                    if (!attempt.sessionId) throw new Error('Session non sauvegardée');
                    set({ _completionAttempt: attempt });
                }
                ({ data: batchData } = await api.post('/qcm/history-batch.php', {
                    scope_id: scopeId, current_session_id: attempt.sessionId,
                    ...(parcoursSlug != null ? { parcours: parcoursSlug } : {}),
                    source: questionSource, origin: historyOrigin, mode, entries,
                    ...(partial ? { partial: true } : {}),
                    last_answer_event: answerEvent,
                }));
            } catch (e) {
                if (isCurrent()) set({ error: e?.response?.data?.message || 'Ton bilan reste en cours. Impossible de l’enregistrer : réessaie.', loading: false });
                return null;
            }

            if (!isCurrent()) return null;
            // Le résultat affiché est celui que le serveur a enregistré, y compris sur un
            // retry après réponse perdue : une question retirée entre-temps (ou après la
            // clôture) ne fait pas diverger l'écran de l'historique.
            const recorded = batchData?.data?.recorded;
            const keptIds = new Set(recorded ? recorded.question_ids.map(Number) : scored.map(q => q.id));
            const kept = questions.filter(q => keptIds.has(Number(q.id)));
            const finalAnswers = { ...correctAnswers };
            // Le snapshot enregistré prime : une clé corrigée entre la clôture et un retry
            // ne doit pas réécrire le verdict de la séance telle qu'elle a été enregistrée.
            for (const [qid, correct] of Object.entries(recorded?.correct || {})) {
                finalAnswers[qid] = { explication: null, ...finalAnswers[qid], correct };
            }
            const retiredCount = questions.length - kept.length;
            set({
                finished: true,
                plannedTotal: partial ? get().questions.length : null,
                correctAnswers: finalAnswers,
                sessionId: null,
                _completionAttempt: null,
                ...(retiredCount || partial ? { questions: kept, questionIds: kept.map(q => q.id), currentIndex: 0, retiredCount } : {}),
            });

            return recorded ? { score: recorded.score, total: recorded.total } : { score, total: scored.length };
        } finally { if (isCurrent()) set({ finalizing: false }); }
    },

    // Demarrer un examen melange (examen blanc aleatoire)
    startExamenMelange: async (scopeId, config = {}) => {
        const questionSource = sourceOf({ isCustom: config.isCustom, isPartner: config.isPartner });
        const playMode = config.playMode || 'quiz';
        // Origine par défaut 'melange'. L'apprentissage guidé passe 'audit-parcours'
        // pour distinguer ses bilans des mélanges libres (cf. session/historique).
        const origin = config.origin || 'melange';
        // Options du Mélange en classique (migration 261) : temps max (secondes) et objectif de score (%).
        const withOptions = playMode === 'quiz' && origin === 'melange';
        set({ _runId: get()._runId + 1, finalizing: false, _completionAttempt: null, retiredCount: 0, loading: true, error: null, finished: false, isReview: false, correctAnswers: null,
              historyId: null, parcoursSlug: config.parcours ?? null, isSmartQuiz: true, mode: playMode, origin, scopeId, questionSource, sessionId: null,
              timeLimit: withOptions && config.timeLimit ? config.timeLimit : null, targetPct: withOptions && config.targetPct ? config.targetPct : null, plannedTotal: null });
        try {
            const { data } = await api.post('/qcm/examen-melange.php', {
                scope_id: scopeId,
                nb_questions: config.nbQuestions || 20,
                ...(config.guided ? { guided: true } : {}),
                // Bilan guidé : le serveur tire dans le programme du QCM du concours (migration 165).
                ...(origin === 'audit-diagnostic' ? { bilan: true } : {}),
                ...(config.parcours != null ? { parcours: config.parcours } : {}),
                // Mélange au programme du concours (migration 210) : parts égales entre les rubriques officielles.
                ...(config.programme ? { programme: true } : {}),
                categories: config.categories || [],
                // Filtre par domaine réel (matiere_ref_id) — distinct de
                // `categories`, qui porte des ids de l'arbre du catalogue. Le
                // diagnostic guidé raisonne en domaines et passe par ici.
                ...(config.categoriesRef?.length ? { matieres_ref: config.categoriesRef } : {}),
                difficulte_min: config.difficulteMin || 1,
                difficulte_max: config.difficulteMax || 3,
                // Mode stratifié (diagnostic guidé) : exactement N questions par
                // catégorie — les questions reviennent avec leur matiere_id.
                ...(config.perCategorie ? { per_categorie: config.perCategorie } : {}),
                // Stratification par sujet (diagnostic guidé couvrant).
                ...(config.perSujet ? { per_sujet: config.perSujet } : {}),
            });

            const questions = data.data.questions.map(q => ({
                ...q,
                reponses: q.reponses ? shuffleArray(q.reponses) : q.reponses,
            }));

            const questionIds = questions.map(q => q.id);

            // Pre-load correct answers for apprentissage mode
            let correctAnswers = null;
            if (playMode === 'apprentissage' && questionIds.length > 0) {
                const { data: aData } = await api.post('/qcm/smart-answers.php', { scope_id: scopeId, ...(get().parcoursSlug != null ? { parcours: get().parcoursSlug } : {}), question_ids: questionIds, source: questionSource });
                correctAnswers = aData.data.answers;
            }

            set({
                quizInfo: { label: 'Quiz transversal', id: questions[0]?.sous_sujet_id || null },
                questionIds,
                questions,
                answers: {},
                currentIndex: 0,
                timeInSeconds: 0,
                correctAnswers,
                docsMap: buildDocsMap(data.data.questions),
                smartQuizMeta: {
                    scope_id: scopeId,
                    quiz_mode: 'examen-melange',
                    filters: data.data.filters,
                },
                loading: false,
            });

            // Save session immediately so it can be resumed
            get()._autoSave();

            return true;
        } catch (err) {
            set({ loading: false, isSmartQuiz: false });
            return false;
        }
    },

    // Reprendre un audit du parcours guidé persisté (mobile → maison).
    // Recharge les questions exactes (par IDs, multi-sous-sujets, avec leur
    // sous_sujet_id) et restaure l'état EN TANT QUE quiz transverse (isSmartQuiz)
    // → finishSmartQuiz regroupe correctement par sous-sujet à la fin.
    resumeSession: async (sessionId, { isCustom = false, isPartner = false, scopeId: requestedScopeId = null, parcours = null } = {}) => {
        const questionSource = sourceOf({ isCustom, isPartner });
        set({ _runId: get()._runId + 1, finalizing: false, _completionAttempt: null, retiredCount: 0, loading: true, error: null, finished: false, isReview: false, correctAnswers: null,
              historyId: null, parcoursSlug: null, isSmartQuiz: true, questionSource });
        try {
            // 1. Session persistée (par id) → IDs, réponses, index, temps, origin, scope
            const { data: sessionData } = await api.get('/qcm/session.php', { params: { session_id: sessionId, scope_id: requestedScopeId || get().scopeId || undefined, ...(parcours != null ? { parcours } : {}) } });
            const saved = sessionData.data;
            if (!saved || !Array.isArray(saved.question_ids) || saved.question_ids.length === 0) {
                set({ loading: false, isSmartQuiz: false });
                return false;
            }
            const scopeId = saved.scope_id;
            const origin = saved.origin;
            const playMode = saved.mode || 'quiz';
            const smartMode = origin.startsWith('smart-') ? origin.slice(6) : origin;

            // 2. Questions exactes (par IDs), ordre d'origine préservé
            const { data: qData } = await api.get('/qcm/questions.php', {
                params: { sous_sujet_id: saved.sous_sujet_id, scope_id: scopeId, question_ids: JSON.stringify(saved.question_ids), ...(parcours != null ? { parcours } : {}) }
            });
            const byId = Object.fromEntries((qData.data.questions || []).map(q => [q.id, q]));
            const questions = saved.question_ids.map(id => byId[id]).filter(Boolean);
            if (questions.length === 0) { set({ loading: false, isSmartQuiz: false }); return false; }

            // 3. Mode apprentissage → précharger les bonnes réponses (endpoint transverse multi-source)
            let correctAnswers = null;
            if (playMode === 'apprentissage') {
                const { data: aData } = await api.post('/qcm/smart-answers.php', { scope_id: scopeId, ...(get().parcoursSlug != null ? { parcours: get().parcoursSlug } : {}), question_ids: questions.map(q => q.id), source: questionSource });
                correctAnswers = aData.data.answers;
            }

            set({
                quizInfo: { label: transverseLabel(origin) || 'Quiz', id: questions[0]?.sous_sujet_id || saved.sous_sujet_id },
                scopeId,
                origin,
                isCustomQuiz: isCustom,
                sessionId: saved.id,
                parcoursSlug: parcours,
                questionIds: questions.map(q => q.id),
                questions,
                answers: saved.answers || {},
                currentIndex: Math.min(saved.current_question_index || 0, questions.length - 1),
                timeInSeconds: saved.time_in_seconds || 0,
                timeLimit: saved.time_limit_seconds || null,
                targetPct: saved.target_pct || null,
                plannedTotal: null,
                correctAnswers,
                mode: playMode,
                docsMap: buildDocsMap(qData.data.questions || []),
                smartQuizMeta: { scope_id: scopeId, quiz_mode: (origin === 'melange' || (origin === 'audit-parcours' || origin === 'audit-diagnostic')) ? 'examen-melange' : smartMode },
                isSmartQuiz: true,
                loading: false,
                responseTimes: {},
                _questionShownAt: Date.now(),
            });
            return true;
        } catch (err) {
            set({ loading: false, isSmartQuiz: false });
            return false;
        }
    },

    // Demarrer un quiz sur les erreurs (never/sometimes/recent)
    startErrorsQuiz: async (scopeId, errorMode = 'never', nbQuestions = 20, { isCustom = false, isPartner = false, categorieId = 0, sousSujetId = 0, playMode = 'quiz', scopeImage = null } = {}) => {
        const questionSource = sourceOf({ isCustom, isPartner });
        const origin = 'errors-' + errorMode;
        set({ _runId: get()._runId + 1, finalizing: false, _completionAttempt: null, retiredCount: 0, loading: true, error: null, finished: false, isReview: false, correctAnswers: null,
              historyId: null, parcoursSlug: null, isSmartQuiz: true, mode: playMode, scopeId, questionSource, sessionId: null,
              origin, smartQuizMeta: { scope_id: scopeId, quiz_mode: origin, breakdown: null } });
        try {
            // Get error question IDs from backend
            const { data } = await api.post('/qcm/errors.php', {
                scope_id: scopeId,
                mode: errorMode,
                categorie_id: categorieId || undefined,
                sous_sujet_id: sousSujetId || undefined, // rejouer les erreurs d'un seul sujet
                for_quiz: true,
                nb_questions: nbQuestions,
            });

            const qIds = data.data.question_ids;
            const anchorSsId = data.data.anchor_sous_sujet_id;

            if (!qIds || qIds.length === 0) {
                set({ loading: false, isSmartQuiz: false });
                return false;
            }

            // Load full questions
            const { data: qData } = await api.get('/qcm/questions.php', {
                params: { sous_sujet_id: anchorSsId, scope_id: scopeId, question_ids: JSON.stringify(qIds) }
            });

            const questions = qData.data.questions.map(q => ({
                ...q,
                reponses: q.reponses ? shuffleArray(q.reponses) : q.reponses,
            }));

            const questionIds = questions.map(q => q.id);

            // Pre-load correct answers for apprentissage mode
            let correctAnswers = null;
            if (playMode === 'apprentissage' && questionIds.length > 0) {
                const { data: aData } = await api.post('/qcm/smart-answers.php', { scope_id: scopeId, ...(get().parcoursSlug != null ? { parcours: get().parcoursSlug } : {}), question_ids: questionIds, source: questionSource });
                correctAnswers = aData.data.answers;
            }

            const ERROR_LABELS = {
                never: 'Jamais réussies',
                sometimes: 'Instables',
                recent: 'Erreurs récentes',
            };

            set({
                quizInfo: { label: ERROR_LABELS[errorMode] || 'Quiz erreurs', id: anchorSsId, matiere_image: scopeImage || null },
                questionIds,
                questions,
                answers: {},
                currentIndex: 0,
                timeInSeconds: 0,
                correctAnswers,
                docsMap: buildDocsMap(qData.data.questions),
                loading: false,
            });

            // Save session immediately
            get()._autoSave();

            return true;
        } catch (err) {
            set({ loading: false, isSmartQuiz: false });
            return false;
        }
    },

    // Mode review depuis l'historique
    startReview: async (historyEntry, scopeId, options = {}) => {
        const reviewScopeId = scopeId || historyEntry.scope_id;
        const isCustom = !!options.isCustom;
        // Source repartie à zéro à chaque relecture : sinon elle garderait celle de la séance précédente
        // (outils admin éditoriaux affichés / masqués à tort, ids qui se recouvrent entre tables).
        const questionSource = sourceOf({ isCustom, isPartner: !!options.isPartner });
        set({ _runId: get()._runId + 1, finalizing: false, _completionAttempt: null, loading: true, scopeId: reviewScopeId, isCustomQuiz: isCustom, questionSource });
        try {
            const sousSujetId = historyEntry.sous_sujet_id;
            const entryQuestionIds = historyEntry.question_ids;


            // Cas groupé (audit/mélange) : une session par sous-sujet → on récupère
            // et fusionne TOUS les snapshots, sinon seules les réponses du 1er
            // sous-sujet seraient restaurées (score faussé).
            const historyIds = Array.isArray(historyEntry.historyIds) && historyEntry.historyIds.length
                ? historyEntry.historyIds
                : [historyEntry.id];

            // Questions courantes + bonnes réponses + snapshots en parallèle
            const [{ data: qData }, { data: aData }, snapshotResponses] = await Promise.all([
                api.get('/qcm/questions.php', {
                    params: { sous_sujet_id: sousSujetId, scope_id: reviewScopeId, question_ids: JSON.stringify(entryQuestionIds), history_ids: JSON.stringify(historyIds) }
                }),
                // Mêmes droits que la consultation : questions des historiques détenus.
                api.get('/qcm/answers.php', { params: { sous_sujet_id: sousSujetId, scope_id: reviewScopeId, question_ids: entryQuestionIds.join(','), history_ids: JSON.stringify(historyIds) } }),
                Promise.all(historyIds.map(id => api.get('/qcm/history.php', { params: { id } }))),
            ]);

            // Snapshot fusionné { qId: { given, correct, version } }
            const snapshot = {};
            for (const resp of snapshotResponses) {
                Object.assign(snapshot, resp.data?.data?.answers_snapshot || {});
            }

            // Restaurer les réponses données
            const answers = {};
            for (const [qId, entry] of Object.entries(snapshot)) {
                answers[qId] = typeof entry === 'object' ? entry.given : entry;
            }

            // Suivi de contenu (migration 180) : question corrigée ou améliorée APRÈS cette séance -> encart en
            // relecture. Dates serveur au même format « AAAA-MM-JJ HH:MM:SS » : la comparaison de chaînes suffit.
            const answeredAt = String(historyEntry.created_at || '');
            const withChange = (q) => (q.content_updated_at && answeredAt && String(q.content_updated_at) > answeredAt)
                ? { ...q, _contentChange: { kind: q.content_change, date: q.content_updated_at, note: q.content_change_note } }
                : q;

            // Déterminer les questions dont le contenu a changé depuis la session
            let questions = qData.data.questions.map(withChange).map(q => {
                const snap = snapshot[q.id];
                if (!snap) return q;
                const snapVersion = snap.version || 1;
                const currentVersion = q.version || 1;
                if (currentVersion <= snapVersion) return q;
                return {
                    ...q,
                    _versionChanged: true,
                    _answeredVersion: snapVersion,
                    _currentVersion: currentVersion,
                };
            });

            let correctAnswers = { ...aData.data.answers };

            // Si des questions ont changé, récupérer le contenu archivé
            const hasChanged = questions.some(q => q._versionChanged);
            if (hasChanged) {
                try {
                    const { data: vcData } = await api.get('/qcm/history-content.php', {
                        params: { history_id: historyEntry.id }
                    });
                    if (vcData.data.changed?.length > 0) {
                        const archivedMap = Object.fromEntries(
                            vcData.data.changed.map(a => [a.question_id, a])
                        );
                        questions = questions.map(q => {
                            const arc = archivedMap[q.id];
                            if (!arc) return q;
                            return {
                                ...q,
                                enonce: arc.enonce,
                                reponses: arc.reponses.filter(r => r.isActive !== false),
                            };
                        });
                        correctAnswers = { ...correctAnswers };
                        for (const arc of vcData.data.changed) {
                            if (correctAnswers[arc.question_id]) {
                                correctAnswers[arc.question_id] = {
                                    ...correctAnswers[arc.question_id],
                                    correct: arc.bonne_reponse,
                                    explication: arc.explication,
                                };
                            }
                        }
                    }
                } catch (e) {
                    console.warn('Contenu archivé indisponible, affichage version courante:', e);
                }
            }

            const distinctReviewSS = new Set(questions.map(q => q.sous_sujet_id).filter(Boolean));
            const tReviewLabel = transverseLabel(historyEntry.origin) || (distinctReviewSS.size > 1 ? 'Quiz transversal' : null);
            set({
                quizInfo: tReviewLabel ? { ...qData.data.sous_sujet, label: tReviewLabel } : qData.data.sous_sujet,
                questionIds: entryQuestionIds,
                questions,
                answers,
                correctAnswers,
                currentIndex: 0,
                timeInSeconds: 0,
                finished: true,
                isReview: true,
                loading: false,
                historyId: historyEntry.id,
            });

            // Marquer comme revu (toutes les sessions du groupe)
            await Promise.all(historyIds.map(id => api.put('/qcm/history.php', { id })));
        } catch (e) {
            console.error('Erreur chargement review:', e);
            set({ loading: false, error: 'Impossible de charger la correction. Veuillez réessayer.' });
        }
    },

    // Reset
    reset: () => {
        clearTimeout(get()._saveTimer);
        set({
            _runId: get()._runId + 1,
            quizInfo: null, scopeId: null, questionIds: [], questions: [], answers: {}, correctAnswers: null,
            currentIndex: 0, timeInSeconds: 0, loading: false, finished: false, finalizing: false, accessDenied: false,
            isReview: false, isSmartQuiz: false, smartQuizMeta: null, timeLimit: null, targetPct: null, plannedTotal: null,
            historyId: null, parcoursSlug: null, mode: 'quiz', origin: 'subject', isCustomQuiz: false, questionSource: 'official', docsMap: null,
            error: null, _completionAttempt: null, retiredCount: 0, responseTimes: {}, _questionShownAt: 0, sessionId: null, _sessionCreating: false,
            _lastAnswerEvent: null, _lastAnswerContext: null,
        });
    },

    // Auto-save debounced (interne)
    _saveTimer: null,
    _saveSessionNow: async ({ forFinalization = false } = {}) => {
        const context = get().quizInfo;
        const runId = get()._runId;
        if (get().finalizing && !forFinalization) return;
        // Plusieurs demandes peuvent attendre la même écriture. Reprendre la
        // file après chaque attente évite deux sauvegardes concurrentes.
        while (get()._saveInFlight) await get()._saveInFlight;
        if (runId !== get()._runId || context !== get().quizInfo || get().finished || get().isReview
            || (get().finalizing && !forFinalization)
            || get()._completionAttempt?.context === context) return;
        const promise = (async () => {
            const { quizInfo, questionIds, answers, currentIndex, timeInSeconds, mode, origin, scopeId, isSmartQuiz, smartQuizMeta, questions, sessionId, _sessionCreating, finished, isReview } = get();
            if (!quizInfo) return;
            // Re-check : un auto-save planifié AVANT la fin (debounce 1s) ne doit pas
            // ressusciter / recréer une session pour un quiz déjà terminé ou en revue.
            if (finished || isReview) return;

            // For smart/error quiz, use first question's sous_sujet_id and appropriate origin
            const sousSujetId = quizInfo.id || (questions[0]?.sous_sujet_id ?? 0);
            // Error/melange quizzes keep their origin, smart quizzes need 'smart-' prefix.
            // (Sert uniquement à l'INSERT ; l'UPDATE par id ne touche pas l'origin.)
            const sessionOrigin = (origin?.startsWith('errors-') || origin === 'melange' || (origin === 'audit-parcours' || origin === 'audit-diagnostic'))
                ? origin
                : (isSmartQuiz ? ('smart-' + (smartQuizMeta?.quiz_mode || 'mixed')) : origin);

            if (!sousSujetId) return;

            // Anti-double-INSERT : si une création est déjà en vol (sessionId pas
            // encore connu), on saute cet auto-save. La prochaine interaction
            // (réponse/navigation/tick) le re-déclenchera, et fera alors un UPDATE.
            if (!sessionId && _sessionCreating) throw new Error('Sauvegarde en cours');

            const creating = !sessionId;
            if (creating) set({ _sessionCreating: true });
            try {
                const { data } = await api.post('/qcm/session.php', {
                    last_answer_event: get()._lastAnswerContext === quizInfo ? get()._lastAnswerEvent : null,
                    session_id: sessionId || undefined,
                    sous_sujet_id: sousSujetId,
                    scope_id: scopeId,
                    question_ids: questionIds,
                    ...(get().parcoursSlug != null ? { parcours: get().parcoursSlug } : {}),
                    answers,
                    current_question_index: currentIndex,
                    time_in_seconds: timeInSeconds,
                    ...(sessionOrigin === 'melange' && get().timeLimit ? { time_limit_seconds: get().timeLimit } : {}),
                    ...(sessionOrigin === 'melange' && get().targetPct ? { target_pct: get().targetPct } : {}),
                    mode,
                    origin: sessionOrigin,
                });
                // Mémoriser l'id renvoyé (INSERT initial, ou re-création si la
                // session ciblée avait disparu) — sauf si le quiz a été terminé/reset
                // entre-temps, ou si on a changé de session.
                const newId = data?.data?.id;
                const s = get();
                if (newId && runId === s._runId && !s.finished && quizInfo === s.quizInfo && (s.sessionId === sessionId || !s.sessionId)) {
                    set({ sessionId: newId });
                }
            } finally {
                if (creating && runId === get()._runId) set({ _sessionCreating: false });
            }
        })();
        set({ _saveInFlight: promise });
        try { await promise; }
        finally { if (get()._saveInFlight === promise) set({ _saveInFlight: null }); }
    },

    _autoSave: () => {
        const state = get();
        if (state.finished || state.finalizing || state.isReview || state._completionAttempt?.context === state.quizInfo) return;

        clearTimeout(state._saveTimer);
        const timer = setTimeout(() => {
            get()._saveSessionNow().catch(e => console.warn('Auto-save session échoué:', e));
        }, 1000);
        set({ _saveTimer: timer });
    },
}));

export default useQuizStore;
